import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Wallet, Plus, ArrowUpRight, ArrowDownRight, Tv, Package,
  Loader2, ChevronLeft, CreditCard, Clock,
  CheckCircle2, AlertTriangle, User, RefreshCw, TicketIcon, Users, Search,
  History, MapPin
} from 'lucide-react';
import CustomerSwitcher, { useActiveCustomer } from '@/components/CustomerSwitcher';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalWallet() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [pendingRecharge, setPendingRecharge] = useState(location.state?.recharge || null);
  const [user, setUser] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [iptvWallet, setIptvWallet] = useState({ balance: 0 });
  const [iptvTransactions, setIptvTransactions] = useState([]);
  const [iptvSubscriptions, setIptvSubscriptions] = useState([]);
  const [hasIptv, setHasIptv] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [packs, setPacks] = useState([]);
  const [activeSubs, setActiveSubs] = useState([]);
  const [pastSubs, setPastSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('packs');
  const [subSearch, setSubSearch] = useState('');
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [showActivate, setShowActivate] = useState(null);
  const [activateForm, setActivateForm] = useState({ phone: '', name: '', email: '' });
  const [activateLoading, setActivateLoading] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const { activeId, setActiveId } = useActiveCustomer(allCustomers);

  useEffect(() => {
    const payment = searchParams.get('payment');
    const amount = searchParams.get('amount');
    if (payment === 'success') toast.success(`Payment successful! Rs.${amount || ''} added to wallet`);
    else if (payment === 'failed') toast.error('Payment failed. Please try again.');
  }, [searchParams]);

  const basePath = window.location.pathname.startsWith('/partner') ? '/partner' : '/portal';

  // When arriving from dashboard "Recharge Now", auto-open the recharge dialog
  // with the subscriber's existing pack pre-selected and mobile pre-filled.
  useEffect(() => {
    if (pendingRecharge && packs.length > 0) {
      const match =
        packs.find(p => p.pack_id === pendingRecharge.planId) ||
        packs.find(p => (p.pack_name || '').toLowerCase() === (pendingRecharge.planName || '').toLowerCase()) ||
        packs[0];
      setTab('packs');
      setShowActivate(match);
      setActivateForm({ phone: pendingRecharge.mobile || '', name: pendingRecharge.name || '', email: '' });
      setPendingRecharge(null);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [packs, pendingRecharge]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const storedUser = localStorage.getItem('portal_user');
    const token = localStorage.getItem('portal_token');
    if (!storedUser || !token) { navigate(basePath); return; }
    const u = JSON.parse(storedUser);
    setUser(u);
    fetchCustomerAndData(u, token);
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCustomerAndData = async (u, token) => {
    try {
      const servicesRes = await fetch(`${API}/api/portal/my-services`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (servicesRes.status === 401) { navigate(basePath); return; }
      const services = await servicesRes.json();
      setAllCustomers(services.customers || []);
      const storedActive = localStorage.getItem('portal_active_customer_id');
      const picked =
        (storedActive && services.customers?.find(c => c.customer_id === storedActive)) ||
        services.customers?.[0];
      const cid = picked?.customer_id;
      const partnerCustomer = services.customers?.find(c => c.customer_type === 'partner' && c.lco_management_enabled);
      setIsPartner(!!partnerCustomer);
      if (cid) {
        setCustomerId(cid);
        fetchAllData(cid);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch customer data:', err);
      setLoading(false);
    }
  };

  const fetchAllData = async (cid) => {
    try {
      const [balRes, txnRes, packsRes, subsRes, iptvBalRes, iptvTxnRes, iptvSubsRes] = await Promise.all([
        fetch(`${API}/api/wallet/portal/balance?customer_id=${cid}`),
        fetch(`${API}/api/wallet/portal/transactions?customer_id=${cid}&limit=30`),
        fetch(`${API}/api/ott/portal/packs?customer_id=${cid}`),
        fetch(`${API}/api/ott/portal/my-subscriptions?customer_id=${cid}`),
        fetch(`${API}/api/iptv/portal/balance?customer_id=${cid}`),
        fetch(`${API}/api/iptv/portal/transactions?customer_id=${cid}&limit=30`),
        fetch(`${API}/api/iptv/portal/subscriptions?customer_id=${cid}`).catch(() => null),
      ]);
      if (balRes.ok) setWallet(await balRes.json());
      if (txnRes.ok) { const data = await txnRes.json(); setTransactions(data.transactions || []); }
      if (packsRes.ok) { const data = await packsRes.json(); setPacks(data.packs || []); }
      if (subsRes.ok) { const data = await subsRes.json(); setActiveSubs(data.active || []); setPastSubs(data.past || []); }
      if (iptvBalRes && iptvBalRes.ok) {
        const d = await iptvBalRes.json();
        setIptvWallet({ balance: d.balance || 0, is_low_balance: d.is_low_balance });
        setHasIptv((d.balance || 0) > 0);
      }
      if (iptvTxnRes && iptvTxnRes.ok) {
        const d = await iptvTxnRes.json();
        setIptvTransactions(d.transactions || []);
        if ((d.transactions || []).length > 0) setHasIptv(true);
      }
      if (iptvSubsRes && iptvSubsRes.ok) {
        const d = await iptvSubsRes.json();
        const subs = d.subscriptions || d.active || [];
        setIptvSubscriptions(subs);
        if (subs.length > 0) setHasIptv(true);
      }
    } catch (err) {
      console.error('Failed to fetch wallet/OTT data:', err);
    }
    setLoading(false);
  };

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (!amount || amount < 1) { toast.error('Enter a valid amount (min Rs.1)'); return; }
    setTopUpLoading(true);
    try {
      const res = await fetch(`${API}/api/wallet/portal/topup/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: customerId, amount })
      });
      const data = await res.json();
      if (data.success && data.payment_url) window.location.href = data.payment_url;
      else toast.error(data.detail || 'Failed to initiate payment');
    } catch { toast.error('Network error'); }
    setTopUpLoading(false);
  };

  const handleActivatePack = async () => {
    if (!activateForm.phone || activateForm.phone.length !== 10) { toast.error('Enter valid 10-digit mobile number'); return; }
    setActivateLoading(true);
    try {
      const res = await fetch(`${API}/api/ott/portal/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pack_id: showActivate.pack_id,
          customer_id: customerId,
          mobile_no: activateForm.phone,
          subscriber_name: activateForm.name,
          subscriber_email: activateForm.email
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Pack activated!');
        setShowActivate(null);
        setActivateForm({ phone: '', name: '', email: '' });
        fetchAllData(customerId);
      } else {
        toast.error(data.detail || 'Activation failed');
      }
    } catch { toast.error('Network error'); }
    setActivateLoading(false);
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-wallet-page">
      <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`${basePath}/dashboard`)} className="p-1 hover:bg-slate-100 rounded-md transition-colors"><ChevronLeft className="h-5 w-5 text-slate-600" /></button>
          <img src="/logo.png" alt="ANT Broadband" className="h-8 w-auto" />
          <div className="flex-1">
            <h1 className="font-bold text-lg text-slate-900 tracking-tight">OTT</h1>
            <p className="text-xs text-slate-500">{user?.phone}</p>
          </div>
          {isPartner && (
            <Button variant="outline" size="sm" onClick={() => navigate(`${basePath}/lcos`)} data-testid="manage-lcos-btn">
              <Users className="h-4 w-4 mr-1" /> LCOs
            </Button>
          )}
          {allCustomers.length > 1 && (
            <CustomerSwitcher customers={allCustomers} activeId={activeId} onChange={setActiveId} />
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-6">
        {/* OTT Wallet Balance Card */}
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white overflow-hidden" data-testid="ott-wallet-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> OTT Wallet</p>
                <p className="text-3xl font-bold mt-1" data-testid="wallet-balance">
                  Rs.{wallet.balance?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </p>
                {wallet.is_low_balance && (
                  <div className="flex items-center gap-1 mt-2 text-amber-400 text-xs"><AlertTriangle className="h-3 w-3" /> Low balance</div>
                )}
              </div>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => setShowTopUp(true)} data-testid="topup-btn">
                <Plus className="h-4 w-4 mr-1" /> Top Up
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {[
            { id: 'packs', label: 'OTT Packs', icon: Tv },
            { id: 'subscriptions', label: 'My Subscriptions', icon: Package },
            { id: 'wallet', label: 'Transactions', icon: Wallet },
            { id: 'activity', label: 'Activity Log', icon: History }
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              data-testid={`portal-tab-${t.id}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* OTT Packs Tab */}
        {tab === 'packs' && (
          <div className="grid gap-4 sm:grid-cols-2">
            {packs.length === 0 ? (
              <Card className="col-span-full">
                <CardContent className="py-8 text-center text-slate-500">
                  <Tv className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                  <p>No OTT packs available yet</p>
                  <p className="text-xs mt-1">Packs will appear here once configured by admin</p>
                </CardContent>
              </Card>
            ) : packs.map(pack => (
              <Card key={pack.pack_id} className="relative" data-testid={`portal-pack-${pack.pack_id}`}>
                <CardHeader className="pb-2">
                  <Badge variant="outline" className="w-fit text-xs mb-1">{pack.provider}</Badge>
                  <CardTitle className="text-base">{pack.pack_name}</CardTitle>
                  <CardDescription className="text-xs">{pack.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-baseline gap-2">
                    {pack.has_discount && <span className="text-sm text-slate-400 line-through">Rs.{pack.base_price}</span>}
                    <span className="text-xl font-bold text-slate-900">Rs.{pack.display_price}</span>
                    <span className="text-sm text-slate-500">/ {pack.validity_days} days</span>
                  </div>
                  {pack.has_discount && <Badge className="bg-emerald-100 text-emerald-700">{pack.discount_percent}% off</Badge>}
                  {pack.channels?.length > 0 && (
                    <div className="flex flex-wrap gap-1">{pack.channels.map(c => <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>)}</div>
                  )}
                  <Button className="w-full mt-2"
                    onClick={() => { setShowActivate(pack); setActivateForm({ phone: user?.phone || '', name: '', email: '' }); }}
                    disabled={wallet.balance < pack.display_price}
                    data-testid={`activate-pack-${pack.pack_id}`}
                  >
                    {wallet.balance < pack.display_price ? 'Insufficient Balance' : (
                      <><Tv className="h-4 w-4 mr-2" /> Add Subscriber - Rs.{pack.display_price}</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Subscriptions Tab */}
        {tab === 'subscriptions' && (() => {
          const q = subSearch.trim().toLowerCase();
          const digits = q.replace(/\D/g, '');
          const matches = (sub) => {
            if (!q) return true;
            return (sub.subscriber_name || '').toLowerCase().includes(q) ||
              (sub.pack_name || '').toLowerCase().includes(q) ||
              (sub.provider_pack_name || '').toLowerCase().includes(q) ||
              (digits && (sub.mobile_no || '').replace(/\D/g, '').includes(digits));
          };
          const filteredActive = activeSubs.filter(matches);
          const filteredPast = pastSubs.filter(matches);
          return (
          <div className="space-y-4">
            {(activeSubs.length > 0 || pastSubs.length > 0) && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search subscriber name, mobile or pack..."
                  value={subSearch}
                  onChange={e => setSubSearch(e.target.value)}
                  className="pl-9 bg-white"
                  data-testid="ott-sub-search-input"
                />
              </div>
            )}
            {q && filteredActive.length === 0 && filteredPast.length === 0 && (
              <Card><CardContent className="py-8 text-center text-slate-500">
                <Search className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                <p>No subscribers match "{subSearch}"</p>
              </CardContent></Card>
            )}
            {filteredActive.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Active Subscriptions ({filteredActive.length}{q ? ` of ${activeSubs.length}` : ''})</h3>
                {filteredActive.map(sub => (
                  <Card key={sub.activation_id} className="border-l-4 border-l-emerald-500" data-testid={`active-sub-${sub.activation_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">{sub.provider}</Badge>
                            {sub.provider_status === 'activated' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs"><CheckCircle2 className="h-3 w-3 mr-0.5 inline" />Activated</Badge>
                            ) : sub.provider_status === 'failed' ? (
                              <Badge className="bg-red-100 text-red-700 text-xs"><AlertTriangle className="h-3 w-3 mr-0.5 inline" />Failed</Badge>
                            ) : sub.provider_status === 'pending' ? (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Pending</Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Active</Badge>
                            )}
                          </div>
                          <p className="font-semibold text-slate-900">{sub.provider_pack_name || sub.pack_name}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {sub.subscriber_name && <><User className="h-3 w-3 inline mr-1" />{sub.subscriber_name} | </>}
                            Mobile: {sub.mobile_no} | {sub.start_date} to {sub.end_date}
                          </p>
                          {sub.provider_expiry && (
                            <p className="text-xs text-slate-400 mt-0.5">
                              Provider Expiry: {new Date(sub.provider_expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                          {sub.provider_error && sub.provider_status === 'failed' && (
                            <p className="text-xs text-red-500 mt-1">{sub.provider_error}</p>
                          )}
                        </div>
                        <p className="text-lg font-bold">Rs.{sub.amount_charged}</p>
                      </div>
                      {/* Retry & Ticket buttons for failed/pending */}
                      {(sub.provider_status === 'failed' || sub.provider_status === 'pending') && (
                        <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                          <Button size="sm" variant="outline" className="text-xs"
                            data-testid={`portal-retry-${sub.activation_id}`}
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API}/api/ott/portal/activations/${sub.activation_id}/retry?customer_id=${customerId}`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' }
                                });
                                const data = await res.json();
                                if (data.success) { toast.success(data.message); fetchAllData(customerId); }
                                else toast.error(data.message || 'Retry failed');
                              } catch { toast.error('Connection failed'); }
                            }}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" /> Retry Activation
                          </Button>
                          {!sub.ticket_raised && (
                            <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`portal-ticket-${sub.activation_id}`}
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API}/api/ott/portal/activations/${sub.activation_id}/raise-ticket`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ customer_id: customerId })
                                  });
                                  const data = await res.json();
                                  if (data.success) { toast.success(data.message); fetchAllData(customerId); }
                                  else toast.error(data.message || data.detail || 'Failed to raise ticket');
                                } catch { toast.error('Connection failed'); }
                              }}
                            >
                              <TicketIcon className="h-3 w-3 mr-1" /> Raise Ticket
                            </Button>
                          )}
                          {sub.ticket_raised && (
                            <Badge variant="outline" className="text-xs text-blue-600 self-center">Ticket Raised</Badge>
                          )}
                        </div>
                      )}
                      {/* OTT Services List */}
                      {sub.provider_otts && sub.provider_otts.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-xs font-medium text-slate-500 mb-2">Included OTT Services ({sub.provider_otts.length})</p>
                          <div className="flex flex-wrap gap-1.5">
                            {sub.provider_otts.map((ott, i) => (
                              <Badge key={ott.ottCode || i} variant="secondary" className={`text-xs ${ott.statusCode === 2 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                {ott.ottName}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
            {filteredPast.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mt-6">Past Subscriptions ({filteredPast.length}{q ? ` of ${pastSubs.length}` : ''})</h3>
                {filteredPast.map(sub => (
                  <Card key={sub.activation_id} className="opacity-75">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">{sub.provider}</Badge>
                            <Badge className="bg-slate-100 text-slate-600 text-xs">{sub.status}</Badge>
                          </div>
                          <p className="font-medium text-slate-700">{sub.provider_pack_name || sub.pack_name}</p>
                          <p className="text-xs text-slate-500">{sub.subscriber_name && `${sub.subscriber_name} | `}{sub.mobile_no} | {sub.start_date} to {sub.end_date}</p>
                        </div>
                        <p className="font-medium text-slate-600">Rs.{sub.amount_charged}</p>
                      </div>
                      {sub.provider_otts && sub.provider_otts.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          <div className="flex flex-wrap gap-1">
                            {sub.provider_otts.map((ott, i) => (
                              <Badge key={ott.ottCode || i} variant="secondary" className="text-xs">{ott.ottName}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
            {activeSubs.length === 0 && pastSubs.length === 0 && (
              <Card><CardContent className="py-8 text-center text-slate-500">
                <Tv className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                <p>No subscriptions yet</p>
                <Button variant="link" className="mt-2" onClick={() => setTab('packs')}>Browse OTT Packs</Button>
              </CardContent></Card>
            )}
          </div>
          );
        })()}

        {/* Transactions Tab — OTT only (IPTV transactions live on the IPTV page) */}
        {tab === 'wallet' && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Wallet className="h-4 w-4" /> OTT Transaction History</CardTitle></CardHeader>
            <CardContent className="p-0">
              {transactions.length === 0 ? (
                <div className="p-8 text-center text-slate-500"><Clock className="mx-auto h-10 w-10 text-slate-300 mb-2" /><p>No OTT transactions yet</p></div>
              ) : (
                <div className="divide-y">
                  {transactions.map(txn => (
                    <div key={txn.transaction_id} className="p-4 flex items-center justify-between" data-testid={`txn-${txn.transaction_id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${txn.type === 'credit' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                          {txn.type === 'credit' ? <ArrowDownRight className="h-4 w-4 text-emerald-600" /> : <ArrowUpRight className="h-4 w-4 text-red-600" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{txn.description}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(txn.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      <span className={`font-semibold ${txn.type === 'credit' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {txn.type === 'credit' ? '+' : '-'}Rs.{txn.amount?.toLocaleString('en-IN')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Activity Log Tab — full audit trail scoped to this user */}
        {tab === 'activity' && <ActivityLogPanel />}
      </main>

      {/* Top Up Dialog */}
      <Dialog open={showTopUp} onOpenChange={setShowTopUp}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Top Up Wallet</DialogTitle>
            <DialogDescription>Add money via online payment</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {[500, 1000, 2000].map(amt => (
                <Button key={amt} variant={topUpAmount === String(amt) ? 'default' : 'outline'} onClick={() => setTopUpAmount(String(amt))} data-testid={`quick-amount-${amt}`}>
                  Rs.{amt}
                </Button>
              ))}
            </div>
            <Input type="number" placeholder="Enter custom amount" value={topUpAmount} onChange={e => setTopUpAmount(e.target.value)} min="1" data-testid="topup-amount-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTopUp(false)}>Cancel</Button>
            <Button onClick={handleTopUp} disabled={topUpLoading || !topUpAmount || parseFloat(topUpAmount) < 1} data-testid="topup-proceed-btn">
              {topUpLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
              Pay Rs.{topUpAmount || '0'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate Pack Dialog */}
      <Dialog open={!!showActivate} onOpenChange={v => { if (!v) setShowActivate(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Subscriber - {showActivate?.pack_name}</DialogTitle>
            <DialogDescription>{showActivate?.provider} | Rs.{showActivate?.display_price} for {showActivate?.validity_days} days</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Package</Label>
              <Select
                value={showActivate?.pack_id || ''}
                onValueChange={(val) => {
                  const p = packs.find(pk => pk.pack_id === val);
                  if (p) setShowActivate(p);
                }}
              >
                <SelectTrigger data-testid="activate-pack-select">
                  <SelectValue placeholder="Select a package" />
                </SelectTrigger>
                <SelectContent>
                  {packs.map(p => (
                    <SelectItem key={p.pack_id} value={p.pack_id} data-testid={`activate-pack-option-${p.pack_id}`}>
                      {p.provider} · {p.pack_name} — Rs.{p.display_price} / {p.validity_days}d
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-slate-50 rounded-md p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Wallet Balance</span><span className="font-semibold">Rs.{wallet.balance?.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between mt-1"><span className="text-slate-500">Pack Cost</span><span className="font-semibold text-red-600">-Rs.{showActivate?.display_price}</span></div>
              <hr className="my-2" />
              <div className="flex justify-between"><span className="text-slate-600 font-medium">After Activation</span><span className="font-bold">Rs.{((wallet.balance || 0) - (showActivate?.display_price || 0)).toFixed(2)}</span></div>
            </div>
            <div>
              <Label>Subscriber Name</Label>
              <Input placeholder="Enter subscriber name" value={activateForm.name} onChange={e => setActivateForm(p => ({ ...p, name: e.target.value }))} data-testid="activate-name-input" />
            </div>
            <div>
              <Label>Email Address <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
              <Input type="email" placeholder="subscriber@email.com" value={activateForm.email} onChange={e => setActivateForm(p => ({ ...p, email: e.target.value }))} data-testid="activate-email-input" />
            </div>
            <div>
              <Label>Mobile Number</Label>
              <Input placeholder="10-digit mobile number" value={activateForm.phone} onChange={e => setActivateForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} maxLength={10} data-testid="activate-phone-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivate(null)}>Cancel</Button>
            <Button onClick={handleActivatePack} disabled={activateLoading} data-testid="confirm-activate-btn">
              {activateLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Activate Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ActivityLogPanel() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('portal_token') || localStorage.getItem('token');
        const res = await fetch(`${API}/api/portal/audit-log?limit=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRows(data.rows || []);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4" /> Activity Log
        </CardTitle>
        <CardDescription>
          Every credit, debit, login and permission change on your account with the IP and location it came from.
          12-month rolling history.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Clock className="mx-auto h-10 w-10 text-slate-300 mb-2" />
            <p>No activity recorded yet.</p>
          </div>
        ) : (
          <div className="divide-y max-h-[600px] overflow-y-auto" data-testid="portal-activity-list">
            {rows.map((r) => (
              <div key={r.audit_id} className="p-3 flex items-start justify-between gap-3 text-sm">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    r.event_type.includes('debit') ? 'bg-red-100' :
                    r.event_type.includes('credit') || r.event_type.includes('topup') ? 'bg-emerald-100' :
                    r.event_type === 'login' ? 'bg-blue-100' : 'bg-slate-100'
                  }`}>
                    {r.event_type.includes('debit') ? <ArrowUpRight className="h-3.5 w-3.5 text-red-600" /> :
                     r.event_type.includes('credit') || r.event_type.includes('topup') ? <ArrowDownRight className="h-3.5 w-3.5 text-emerald-600" /> :
                     r.event_type === 'login' ? <User className="h-3.5 w-3.5 text-blue-600" /> :
                     <Clock className="h-3.5 w-3.5 text-slate-500" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {r.description || r.event_type.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                      <span>{new Date(r.created_at).toLocaleString()}</span>
                      {r.wallet_type && <Badge variant="outline" className="text-[10px] py-0">{r.wallet_type.toUpperCase()}</Badge>}
                      {r.source && r.source !== 'online' && <Badge variant="outline" className="text-[10px] py-0">{r.source}</Badge>}
                    </p>
                    {(r.ip || r.location) && (
                      <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3" />
                        <span className="font-mono">{r.ip || '—'}</span>
                        {r.location && (
                          <span>· {[r.location.city, r.location.country_code].filter(Boolean).join(', ')}</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                {r.amount != null && (
                  <span className={`font-semibold whitespace-nowrap ${
                    r.event_type.includes('debit') ? 'text-red-600' :
                    r.event_type.includes('credit') || r.event_type.includes('topup') ? 'text-emerald-600' : 'text-slate-500'
                  }`}>
                    {r.event_type.includes('debit') ? '-' : '+'}Rs.{Number(r.amount).toLocaleString('en-IN')}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

